import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import type { FirestoreSshKey } from '@jessie/commons';
import { isValidSshPublicKey, SshKeyService } from '../../../services/ssh-key.service';
import { SettingsCardComponent } from '../settings-card/settings-card.component';
import { TextInputComponent } from '../../../components/input/text-input/text-input.component';


@Component({
  selector: 'app-ssh-keys-card',
  templateUrl: './ssh-keys-card.component.html',
  imports: [SettingsCardComponent, ReactiveFormsModule, TextInputComponent, DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SshKeysCardComponent implements OnInit {
  @Input() orgaId!: string;
  @Input() orgaUserId!: string;

  private sshKeyService = inject(SshKeyService);

  keys$ = signal<FirestoreSshKey[]>([]);
  showAddForm = signal(false);
  addError = signal<string | null>(null);
  isSubmitting = signal(false);

  addForm = new FormGroup({
    publicKey: new FormControl('', [Validators.required]),
    label: new FormControl('', [Validators.required, Validators.maxLength(100)]),
  });

  ngOnInit() {
    this.sshKeyService.getKeys(this.orgaId, this.orgaUserId).subscribe((keys) => this.keys$.set(keys));
  }

  toggleAddForm() {
    this.showAddForm.set(!this.showAddForm());
    if (!this.showAddForm()) {
      this.addForm.reset();
      this.addError.set(null);
    }
  }

  async submitKey() {
    const publicKey: string = this.addForm.value.publicKey?.trim() ?? '';
    const label: string = this.addForm.value.label?.trim() ?? '';

    if (!publicKey || !label) return;
    if (!isValidSshPublicKey(publicKey)) {
      this.addError.set(
        'Ungültiger SSH Public Key. Erlaubte Formate: ssh-ed25519, ssh-rsa, ecdsa-*'
      );
      return;
    }

    this.isSubmitting.set(true);
    this.addError.set(null);

    try {
      await this.sshKeyService.addKey(this.orgaId, this.orgaUserId, publicKey, label);
      this.addForm.reset();
      this.showAddForm.set(false);
    } catch (err) {
      const message = err instanceof Error && err.message === 'This SSH key has already been added.'
        ? 'Dieser SSH-Schlüssel wurde bereits hinzugefügt.'
        : 'Fehler beim Speichern des Schlüssels. Bitte versuche es erneut.';
      this.addError.set(message);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  async deleteKey(key: FirestoreSshKey) {
    await this.sshKeyService.deleteKey(this.orgaId, this.orgaUserId, key.id);
  }

  truncateFingerprint(fingerprint: string): string {
    // Show "SHA256:abcde...xyz" — first 10 and last 6 chars of the hash part
    const prefix = 'SHA256:';
    if (!fingerprint.startsWith(prefix)) return fingerprint;
    const hash = fingerprint.slice(prefix.length);
    if (hash.length <= 16) return fingerprint;
    return `${prefix}${hash.slice(0, 10)}…${hash.slice(-6)}`;
  }
}
